<template>
	<page-container :page="page" :locale="locale" class="index">
		<heading-image v-if="page.headingImage">
			<hero-image type="fullscreen">
				<div v-if="defaults[locale].homepage.headingImageTitle" class="hero-content">
					<p>
						<span>{{ defaults[locale].homepage.headingImageTitle }}</span>
					</p>
					<div v-parse-links v-html="defaults[locale].homepage.headingImageContent ?? ''" />
				</div>
				<div class="slider-overlay" />
				<picture>
					<source
						v-if="page.headingImagePortraitWebp"
						:srcset="page.headingImagePortraitWebp"
						media="(max-width: 500px)"
						type="image/webp"
					/>
					<source
						v-if="page.headingImagePortrait"
						:srcset="page.headingImagePortrait"
						media="(max-width: 500px)"
					/>
					<source :srcset="page.headingImageWebp" type="image/webp" />
					<source :srcset="page.headingImage" />
					<img class="object-fit slider-image" :src="page.headingImage" :alt="page.headingImageAlt" />
				</picture>
			</hero-image>

			<booking-form :booking-form="defaults[locale].bookingForm" />
		</heading-image>

		<main-content :title="page.title || page.header" :subtitle="page.subtitle" :content="page.content" />

		<promoblocks :items="page.promoblocks">
			<template #blocks-header>
				<div v-if="defaults[locale].homepage.sectionPromotionTitle" class="row blocks-header align-center">
					<div class="columns column12">
						<h2>{{ defaults[locale].homepage.sectionPromotionTitle }}</h2>
						<div v-parse-links v-html="defaults[locale].homepage.sectionPromotionContent" />
					</div>
				</div>
			</template>
		</promoblocks>

		<section v-if="defaults[locale].homepage.benefit.length" class="bookingbenefits">
			<div class="row">
				<div class="columns column12 align-center">
					<h2>{{ defaults[locale].homepage.benefitsHeader }}</h2>
				</div>
				<div v-for="benefit in defaults[locale].homepage.benefit" :key="benefit.ID" class="columns column4">
					<font-awesome-icon icon="fas fa-check" size="1x" />
					<div v-parse-links v-html="benefit.content" />
				</div>
			</div>
			<div class="row">
				<div class="columns column12 align-center">
					<book-button class="button book-now cta">
						<span>{{ defaults[locale].website.book }}</span>
					</book-button>
				</div>
			</div>
		</section>

		<contentblocks :items="page.contentblocks" />

		<frequently-asked-questions
			v-if="page.frequentlyAskedQuestion"
			:items="page.frequentlyAskedQuestion.faqitems"
			:header="page.frequentlyAskedQuestion.header"
			class="has-bgimage"
		>
			<template #bgimage>
				<div class="button-row row">
					<div class="columns column12">
						<nuxt-link :to="localePath('faq')" class="button no-fill">
							{{ $t('readMore') }}
						</nuxt-link>
					</div>
				</div>
				<div class="faq-background">
					<div
						v-if="defaults[locale].homepage.bannerImageWebp"
						class="faq-background-image"
						:style="'background-image: url(' + defaults[locale].homepage.bannerImageWebp + ');'"
					/>
					<div
						v-else
						class="faq-background-image"
						:style="'background-image: url(' + defaults[locale].homepage.bannerImage + ');'"
					/>
				</div>
			</template>
		</frequently-asked-questions>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localePath = useLocalePath();

await fetchPage();
await fetchDefaults();
</script>

<style lang="scss" scoped>
.slider-overlay {
	position: absolute;
	inset: 0;
	z-index: 2;
	background: rgb(0 0 0 / 25%);
}

.intro .column8 {
	padding: 20px 60px;
}

.bookingbenefits {
	h2 {
		margin: 0 0 30px;
	}

	.book-now {
		margin: 40px auto 0;
	}

	.column4 {
		text-align: center;
		display: flex;
		flex-flow: row nowrap;
		justify-content: center;
		align-items: center;
	}

	p,
	:deep(p) {
		margin: 0;
	}

	svg {
		margin: 0 10px 0 0;
		color: var(--cta-color);
		font-size: 28px;
	}
}

.blocks-header {
	.column12 {
		flex-flow: column wrap;
	}
}

.hero-content {
	position: absolute;
	max-width: 820px;
	z-index: 10;
	left: 0;
	right: 0;
	width: auto;
	bottom: 50%;
	margin: 0 auto -90px;
	text-align: center;
	color: #fff;
	letter-spacing: 3px;

	:deep(span) {
		color: #fff;
		font-size: var(--hero-heading-font-size);
		font-family: var(--hero-heading-font-family);
		font-weight: 500;
		margin: 0 0 10px;
		width: 100%;
		display: inline-block;
		line-height: var(--hero-heading-line-height);
		letter-spacing: var(--h1-letter-spacing);
	}

	:deep(p) {
		font-size: 1.8em;
	}
}

.faq-section {
	.object-fit {
		position: absolute;
		inset: 0;
		z-index: 1;
	}

	.button-row {
		position: relative;
		z-index: 3;
		text-align: center;
		margin: 45px auto 0;

		.button {
			color: #fff;
			border-color: #fff;
		}
	}
}

.faq-background {
	position: absolute;
	inset: 0;
	z-index: 1;
	container: faq-background / size;
	overflow: hidden;
}

.faq-background-image {
	position: absolute;
	inset: 0;
	background-attachment: fixed;
	background-position: center center;
	background-repeat: no-repeat;
	background-size: auto 100%;
}

@container faq-background (min-aspect-ratio: 3 / 1) {
	.faq-background-image {
		background-size: 100% auto;
	}
}

@media (max-width: 1080px) {
	.bookingbenefits {
		max-width: 600px;
		margin: 0 auto;
		text-align: center;

		.column4 {
			width: 100%;
			margin: 10px auto;
			text-align: left;
			justify-content: flex-start;
		}
	}
}

@media (max-width: 880px) {
	.hero-content {
		margin: 0 auto -100px;

		:deep(span) {
			font-size: 40px;
			line-height: 50px;
		}

		:deep(p) {
			font-size: 30px;
			padding: 0 10px;
		}
	}

	.bookingbenefits .column4 {
		margin: 10px auto;
	}

	.faq-background-image {
		background-attachment: scroll;
	}
}

@media (max-width: 680px) {
	.bookingbenefits {
		.column4 {
			width: calc(100% - 40px);
			text-align: left;
			justify-content: flex-start;
			max-width: 400px;
			margin: 10px auto;
		}
	}
}
</style>
